/**
 * Extract the backend's error code and human message from a thrown API error.
 *
 * The backend's ApplicationExceptionFilter sends `{ error: { code, message, errorCode?, details? } }`.
 * The generated API client throws its HttpResponse on a non-2xx, and that response's `.error`
 * property holds the parsed body — so the real fields live one level deeper than a naive
 * `err.error.message`. Reading too shallow (the old per-screen helpers did) makes every extraction
 * return undefined, which silently falls back to a generic message and never maps an error code.
 * Mirror the working extraction in `trade-link-errors.ts`.
 */

/** The launch initData outlived its server-side lifetime. Terminal — Telegram
 *  only mints a fresh one when the mini app is opened again. */
export const INIT_DATA_EXPIRED = 'INIT_DATA_EXPIRED';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** The inner `{ code, message, errorCode, details }` object, dug out of whatever the client threw. */
function innerError(err: unknown): Record<string, unknown> | undefined {
  if (!isRecord(err)) return undefined;
  // The thrown value is usually the HttpResponse whose `.error` is the parsed body `{ error: {...} }`;
  // occasionally a caller passes the body (or the inner object) directly.
  const candidate = isRecord(err.error) ? err.error : err;
  if (isRecord(candidate.error)) return candidate.error; // wrapper / body → step into `.error`
  // already the inner object: accept only if it carries a code (a plain Error has message but none)
  if (typeof candidate.code === 'string' || typeof candidate.errorCode === 'string') return candidate;
  return undefined;
}

/** The domain error code: the specific `errorCode` (e.g. GAME_NOT_OPEN, MULTIACC_NOT_ALLOWED) when
 *  present, else the generic `code` (e.g. INSUFFICIENT_RESOURCES, VALIDATION_ERROR). */
export function apiErrorCode(err: unknown): string | undefined {
  const inner = innerError(err);
  if (!inner) return undefined;
  if (typeof inner.errorCode === 'string') return inner.errorCode;
  if (typeof inner.code === 'string') return inner.code;
  return undefined;
}

/** The server's human-readable message, or undefined when the thrown value has no API error body. */
export function apiErrorMessage(err: unknown): string | undefined {
  const inner = innerError(err);
  return inner && typeof inner.message === 'string' ? inner.message : undefined;
}

/** The `details` payload the backend attaches to a domain error (e.g. `{ rule: 'MULTIACC_...' }`). */
export function apiErrorDetails(err: unknown): Record<string, unknown> | undefined {
  const details = innerError(err)?.details;
  return isRecord(details) ? details : undefined;
}

/**
 * A compact, loggable one-liner for a thrown API error.
 *
 * The generated client throws its HttpResponse, which `console.error(msg, err)` — and thus
 * Datadog RUM — serialises to the useless string `"[Response]"`, so every failed task looks
 * identical and triage is blind. This pulls out the HTTP status and the backend's error code /
 * message instead. Pass it as the second `console.error` argument:
 *   console.error('Failed to claim daily bonus:', formatApiError(err));
 */
export function formatApiError(err: unknown): string {
  const status = isRecord(err) && typeof err.status === 'number' ? err.status : undefined;
  const statusText = isRecord(err) && typeof err.statusText === 'string' ? err.statusText : undefined;
  const code = apiErrorCode(err);
  const message = apiErrorMessage(err);

  const parts: string[] = [];
  // Fold the HTTP statusText into the status token only when the backend gave us no
  // richer message — otherwise `HTTP 502 Bad Gateway` stays whole and readable.
  if (status !== undefined) parts.push(statusText && !message ? `HTTP ${status} ${statusText}` : `HTTP ${status}`);
  if (code) parts.push(code);
  if (message) parts.push(message);
  if (parts.length > 0) return parts.join(' — ');

  // No status at all (network failure, aborted fetch, or a thrown Error).
  if (err instanceof Error) return `${err.name}: ${err.message}`;
  return String(err);
}
